import React, { useState, useEffect, useContext } from "react";
import * as S from "./NewGoldenBoxStyle";
import {
  FaTimes,
  FaSearch,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft,
} from "react-icons/fa";
import { formatNumber, formatDateSystem } from "../../../assets/utils";
import { goldenBoxService } from "../../../Services/goldenBoxService";
import { AuthContext } from "../../../context/AuthContext";
import Terms from "./Terms";

const getFormattedDateSP = () => {
  const now = new Date();
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  };
  return now.toLocaleDateString("pt-BR", options);
};

export default function NewGoldenBox({
  isOpen,
  onClose,
  initialContractId,
  onSuccess,
}) {
  const { userData } = useContext(AuthContext);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedModalidade, setSelectedModalidade] = useState(0);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  const [step, setStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const todayDate = getFormattedDateSP();

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 3000);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchContracts = async () => {
      if (!userData?.id || !isOpen) return;
      try {
        setLoading(true);
        const data = await goldenBoxService.getAvailableContractsForGoldenBox(
          userData.id
        );
        if (!isMounted) return;
        setContracts(data || []);

        if (initialContractId) {
          const found = (data || []).find(
            (c) => (c.contractId || c.id) === initialContractId
          );
          if (found) {
            setSelectedContract(found);
            if (
              found.avaliableForAnualGoldenBoxType &&
              !found.avaliableForEndGoldenBoxType
            )
              setSelectedModalidade(2);
            else if (
              !found.avaliableForAnualGoldenBoxType &&
              found.avaliableForEndGoldenBoxType
            )
              setSelectedModalidade(1);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchContracts();
    return () => {
      isMounted = false;
      if (!isOpen) {
        setContracts([]);
        setSelectedContract(null);
        setSearchTerm("");
        setCurrentPage(1);
        setStep(1);
        setSelectedModalidade(0);
        setTermsAccepted(false);
        setCreating(false);
      }
    };
  }, [isOpen, userData?.id, initialContractId]);

  const filteredContracts = (contracts || []).filter((c) => {
    const idString = (c.contractId || c.id || "").toString();
    const matchesSearch = idString.includes(searchTerm);
    if (selectedModalidade === 2)
      return matchesSearch && c.avaliableForAnualGoldenBoxType;
    if (selectedModalidade === 1)
      return matchesSearch && c.avaliableForEndGoldenBoxType;
    return false;
  });

  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const currentItems = filteredContracts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleProceed = () => {
    if (selectedContract && selectedModalidade !== 0) setStep(2);
  };

  const handleConfirm = async () => {
    if (!selectedContract || !termsAccepted) return;
    try {
      setCreating(true);
      await goldenBoxService.createGoldenBox({
        clientId: userData.id,
        contractId: selectedContract.contractId || selectedContract.id,
        goldenOptionId: selectedModalidade,
      });
      showToast("Aplicação feita com sucesso!", "success");
      if (onSuccess) onSuccess();
      setTimeout(onClose, 2000);
    } catch (error) {
      showToast("Erro ao processar.", "error");
    } finally {
      setCreating(false);
    }
  };

  const simulacao = (() => {
    if (!selectedContract) return { texto: "", valor: 0 };
    const valorBase =
      selectedContract.contractTotalPrice || selectedContract.totalPrice || 0;
    return selectedModalidade === 1
      ? { texto: "Saque mensal (Plano Total)", valor: valorBase * 0.013 }
      : { texto: "Saque mensal (Plano Anual)", valor: valorBase * 0.01 };
  })();

  if (!isOpen) return null;

  return (
    <S.Overlay onClick={onClose}>
      {toast.show && (
        <S.ToastContainer type={toast.type}>
          <span>{toast.message}</span>
          <button onClick={() => setToast({ ...toast, show: false })}>
            <FaTimes />
          </button>
        </S.ToastContainer>
      )}

      <S.Content onClick={(e) => e.stopPropagation()}>
        <S.Header>
          <h2>{step === 1 ? "Adicionar Contrato" : "Confirmar Aplicação"}</h2>
          <button onClick={onClose} disabled={creating}>
            <FaTimes />
          </button>
        </S.Header>

        <S.Body>
          {step === 1 ? (
            <>
              <label className="section-label">
                1. Escolha a sua modalidade:
              </label>
              <div
                style={{ display: "flex", gap: "10px", marginBottom: "20px" }}
              >
                <S.OptionCard
                  active={selectedModalidade === 2}
                  onClick={() => {
                    setSelectedModalidade(2);
                    setSelectedContract(null);
                  }}
                >
                  <strong>PLANO ANUAL</strong>
                  <span>Saque Mensal 1.0%</span>
                </S.OptionCard>
                <S.OptionCard
                  active={selectedModalidade === 1}
                  onClick={() => {
                    setSelectedModalidade(1);
                    setSelectedContract(null);
                  }}
                >
                  <strong>PLANO TOTAL</strong>
                  <span>Saque Mensal 1.3%</span>
                </S.OptionCard>
              </div>

              {selectedModalidade !== 0 ? (
                <>
                  <div className="search-box">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Pesquisar ID do contrato..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <label className="section-label">
                    2. Selecione um contrato elegível:
                  </label>
                  {loading ? (
                    <S.LoadingContainer>
                      <FaSpinner className="spinner" />
                      <p>Buscando...</p>
                    </S.LoadingContainer>
                  ) : filteredContracts.length === 0 ? (
                    <p className="empty-msg">
                      Nenhum contrato disponível para esta modalidade.
                    </p>
                  ) : (
                    <>
                      <S.ContractList>
                        {currentItems.map((c) => (
                          <S.ContractOption
                            key={c.contractId || c.id}
                            selected={
                              (selectedContract?.contractId ||
                                selectedContract?.id) === (c.contractId || c.id)
                            }
                            onClick={() => setSelectedContract(c)}
                          >
                            <div className="info">
                              <h4>Contrato #{c.contractId || c.id}</h4>
                              <p>
                                Criado em:{" "}
                                {c.dateCreated
                                  ? formatDateSystem(c.dateCreated)
                                  : "Recém criado"}
                              </p>
                            </div>
                            <div className="value">
                              R${" "}
                              {formatNumber(
                                c.contractTotalPrice || c.totalPrice || 0
                              )}
                            </div>
                          </S.ContractOption>
                        ))}
                      </S.ContractList>
                      {totalPages > 1 && (
                        <S.PaginationContainer>
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <FaChevronLeft />
                          </button>
                          <span>
                            Página {currentPage} de {totalPages}
                          </span>
                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <FaChevronRight />
                          </button>
                        </S.PaginationContainer>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "#666",
                    marginTop: "40px",
                  }}
                >
                  Selecione uma das opções acima para visualizar seus contratos
                  disponíveis.
                </p>
              )}
            </>
          ) : (
            <S.StepContent>
              <S.SimulationBox>
                <p>
                  Ao aplicar o{" "}
                  <strong>
                    Contrato #
                    {selectedContract?.contractId || selectedContract?.id}
                  </strong>{" "}
                  você terá direito a:
                </p>
                <div className="value-row">
                  <span>{simulacao.texto}</span>
                  <strong>+ R$ {formatNumber(simulacao.valor)}</strong>
                </div>
              </S.SimulationBox>
              <label className="section-label">
                Termos e Condições da Golden Box
              </label>
              <S.TermsScrollBox>
                <Terms
                  todayDate={todayDate}
                  userData={userData}
                  selectedContract={selectedContract}
                />
              </S.TermsScrollBox>
              <S.CheckboxLabel>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>Li e aceito os termos do contrato de aplicação.</span>
              </S.CheckboxLabel>
            </S.StepContent>
          )}
        </S.Body>

        <S.Footer>
          {step === 1 ? (
            <S.ActionButton
              primary
              disabled={!selectedContract || selectedModalidade === 0}
              onClick={handleProceed}
            >
              PROSSEGUIR
            </S.ActionButton>
          ) : (
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <S.BackButton onClick={() => setStep(1)} disabled={creating}>
                <FaArrowLeft /> Voltar
              </S.BackButton>
              <S.ActionButton
                primary
                disabled={!termsAccepted || creating}
                onClick={handleConfirm}
                style={{ flex: 1 }}
              >
                {creating ? "Processando..." : "CONFIRMAR APLICAÇÃO"}
              </S.ActionButton>
            </div>
          )}
        </S.Footer>
      </S.Content>
    </S.Overlay>
  );
}
